<!--
  AI:
  Status: "in progress"
  Type: Component
  SubType: ProtectedComponent
  Reason: Display-only summary card for a LogicFlowTrace header. Renders
          status badge, timing, level/mode chips, step counts and an optional
          error banner using PrimeNG + PrimeFlex only — no custom CSS.
-->
@if (trace) {
    <p-card>

        <!-- Header row: status badge + flow name + trace id, plus inline
             timing metrics on the left followed by the Trace-Level / Modus
             chips on the right. -->
        <!-- TM-1000001563: status pill driven by BE-supplied enum metadata
             (statusEnumName / Image / Color / ColorBackground). -->
        <!-- TM-1000001563 (lg-trace-summary Issue 3): timing metrics moved
             from the dedicated stats grid below into this header row.
             Each metric renders as a compact inline `Label: value` pair
             (text-color-secondary label + text-sm value) sitting before
             the Trace-Level / Modus chips on the right side. The dedicated
             stats grid that previously stacked Startzeit / Endzeit / Dauer
             vertically below was removed in the same change. -->
        <div class="flex align-items-center justify-content-between flex-wrap gap-2">
            <div class="flex align-items-center gap-2 flex-wrap">
                @if (trace.statusEnumName) {
                    <p-tag
                        [value]="trace.statusEnumName"
                        [icon]="trace.statusEnumImage"
                        [styleClass]="'bg-' + trace.statusEnumColorBackground + ' text-' + trace.statusEnumColor">
                    </p-tag>
                }
                <span class="text-xl font-medium">
                    {{ trace.logicFlowDtoName || 'Logik-Flow' }}
                </span>
                <span class="text-color-secondary text-sm">
                    Trace #{{ trace.id }}
                </span>
            </div>

            <div class="flex align-items-center gap-3 flex-wrap">
                <!-- TM-1000001563 (lg-trace-summary Issue 3): inline
                     timing metrics. Each pair uses a small secondary
                     label and a regular value so the row stays compact
                     and visually balanced with the chips on the right. -->
                <span class="flex align-items-center gap-1">
                    <span class="text-color-secondary text-xs">Startzeit:</span>
                    <span class="text-sm">{{ (trace.startedAt | date:'dd.MM.yyyy HH:mm:ss') || '–' }}</span>
                </span>
                <span class="flex align-items-center gap-1">
                    <span class="text-color-secondary text-xs">Endzeit:</span>
                    <span class="text-sm">{{ (trace.completedAt | date:'dd.MM.yyyy HH:mm:ss') || '–' }}</span>
                </span>
                <span class="flex align-items-center gap-1">
                    <span class="text-color-secondary text-xs">Dauer:</span>
                    <span class="text-sm">{{ formattedDuration }}</span>
                </span>

                <p-chip
                    [label]="'Trace-Level: ' + traceLevelLabel"
                    icon="fa-regular fa-bug">
                </p-chip>
                <p-chip
                    [label]="'Modus: ' + executionModeLabel"
                    icon="fa-regular fa-play">
                </p-chip>
            </div>
        </div>

        <!-- Caller (only when trace was triggered by a known entity) -->
        @if (trace.callerObjectTypeDtoName && trace.callerObjectId != null) {
            <div class="flex align-items-center gap-2 mt-3">
                <span class="text-color-secondary text-sm">Aufgerufen von:</span>
                <p-chip
                    [label]="callerLabel"
                    icon="fa-regular fa-link"
                    styleClass="cursor-pointer"
                    (click)="navigateToCaller()"
                    pTooltip="Zum Aufrufer springen"
                    tooltipPosition="top">
                </p-chip>
            </div>
        }

        <!-- Optional trigger description -->
        @if (trace.triggerDescription) {
            <div class="flex align-items-center gap-2 mt-3 text-color-secondary text-sm">
                <i class="fa-regular fa-tag"></i>
                <span>{{ trace.triggerDescription }}</span>
            </div>
        }

        <!-- Error banner (only on failure with a message) -->
        @if (trace.errorMessage) {
            <div class="flex align-items-start gap-2 mt-3 p-3 border-round border-1 border-red-200 bg-red-50">
                <i class="fa-regular fa-triangle-exclamation text-red-500 mt-1"></i>
                <div class="flex flex-column gap-1">
                    <span class="text-sm font-medium text-red-700">Fehlermeldung</span>
                    <span class="text-sm text-red-700">{{ trace.errorMessage }}</span>
                </div>
            </div>
        }

    </p-card>
}
